import React from "react";
import { ChartDataResponse } from "../ceo-dashboard.service";

interface InflowsOutflowsCardsProps {
  chartData: ChartDataResponse | null;
  loading: boolean;
}

const InflowsOutflowsCards: React.FC<InflowsOutflowsCardsProps> = ({
  chartData,
  loading,
}) => {
  // Calculate totals from the chart data
  const calculateTotalByType = (type: string) => {
    if (!chartData?.data) return 0;
    return chartData.data
      .filter((item) => item.trx_type === type)
      .reduce((sum, item) => sum + parseFloat(item.amount_kwd), 0);
  };

  const totalDeposit = calculateTotalByType("Deposit");
  const totalWithdrawal = calculateTotalByType("Withdrawal");
  const totalSubscription = calculateTotalByType("Subscription");
  const totalRedemption = calculateTotalByType("Redemption");

  // Calculate Inflows and Outflows
  const inflow = totalSubscription + totalDeposit;
  const outflow = totalWithdrawal + totalRedemption;
  const netflow = inflow - outflow;

  return (
    <div className="w-full lg:w-[30rem] flex flex-col justify-start items-start gap-5 lg:h-[420px]">
      <div className="w-full p-4 bg-white rounded-lg inline-flex flex-col justify-between items-start shadow-md flex-1">
        <div className="self-stretch inline-flex justify-between items-start">
          <div className="flex-1 inline-flex flex-col justify-start items-start gap-3.5">
            <div className="self-stretch opacity-70 justify-start text-neutral-800 text-xs font-semibold">
              Inflow = total subscriptions + total deposits
            </div>
            <div className="self-stretch justify-start">
              <span className="text-zinc-800 text-2xl font-bold tracking-wide">
                {loading ? "Loading..." : inflow.toLocaleString()}{" "}
              </span>
              <span className="text-zinc-800 text-base font-semibold tracking-wide">
                KWD
              </span>
            </div>
          </div>
          <img src="/images/total-deposit.svg" alt="Inflow" />
        </div>
      </div>

      <div className="w-full p-4 bg-white rounded-lg inline-flex flex-col justify-between items-start shadow-md flex-1">
        <div className="self-stretch inline-flex justify-between items-start">
          <div className="flex-1 inline-flex flex-col justify-start items-start gap-3.5">
            <div className="self-stretch opacity-70 justify-start text-neutral-800 text-xs font-semibold">
              Outflow = total withdrawals + total redemptions
            </div>
            <div className="self-stretch justify-start">
              <span className="text-zinc-800 text-2xl font-bold tracking-wide">
                {loading ? "Loading..." : outflow.toLocaleString()}{" "}
              </span>
              <span className="text-zinc-800 text-base font-semibold tracking-wide">
                KWD
              </span>
            </div>
          </div>
          <img src="/images/total-withdraw.svg" alt="Outflow" />
        </div>
      </div>

      <div className="w-full p-4 bg-white rounded-lg inline-flex flex-col justify-between items-start shadow-md flex-1">
        <div className="self-stretch inline-flex justify-between items-start">
          <div className="flex-1 inline-flex flex-col justify-start items-start gap-3.5">
            <div className="self-stretch opacity-70 justify-start text-neutral-800 text-xs font-semibold">
              Netflow = Inflow - Outflow
            </div>
            <div className="self-stretch justify-start">
              <span
                className={`text-2xl font-bold tracking-wide ${
                  netflow >= 0 ? "text-green-600" : "text-red-600"
                }`}
              >
                {loading ? "Loading..." : netflow.toLocaleString()}{" "}
              </span>
              <span className="text-zinc-800 text-base font-semibold tracking-wide">
                KWD
              </span>
            </div>
          </div>
          <img src="/images/total-redemption.svg" alt="Net Flow" />
        </div>
      </div>
    </div>
  );
};

export default InflowsOutflowsCards;
